/**
 * useInstallPrompt — Jobsite Capture Arc Stage 2C (2026-06-15).
 *
 * Cross-platform PWA install detection + prompt. Honest about each
 * platform's capability:
 *
 *   • Chromium (Android Chrome / Edge): `beforeinstallprompt` fires when
 *     the app is installable. We capture the event and expose
 *     `promptInstall()` so a button click can call `.prompt()`.
 *
 *   • iOS Safari: NO `beforeinstallprompt` event. Detection is
 *     user-agent-based + `navigator.standalone` (set to true when
 *     launched from the home-screen icon). UI shows manual
 *     Share → Add to Home Screen instructions instead of an automated
 *     prompt.
 *
 *   • Firefox: install UX is not standardized; treat like iOS (manual
 *     instructions, no auto-prompt). Detection: !beforeinstallprompt
 *     event and !iOS.
 *
 * Dismissal: writes `keymade-install-dismissed-until` to localStorage
 * with a future timestamp so callers can hide the prompt for N days
 * without re-querying. The dismissal honors privacy (no analytics).
 */
import { useEffect, useState, useCallback } from 'react';

const DISMISS_KEY = 'keymade-install-dismissed-until';
const DISMISS_DAYS_DEFAULT = 7;

function isIOS() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  // iPad on iOS 13+ reports as Mac; check for touch.
  return /iPad|iPhone|iPod/.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1);
}

function isMacSafari() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|Chromium|Edg|Firefox/.test(ua) && navigator.maxTouchPoints <= 1;
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
  // iOS-specific: Safari sets navigator.standalone when launched from
  // the home screen.
  if (typeof navigator !== 'undefined' && navigator.standalone === true) return true;
  return false;
}

function dismissedRecently() {
  try {
    const until = localStorage.getItem(DISMISS_KEY);
    if (!until) return false;
    return Date.now() < parseInt(until, 10);
  } catch (_err) {
    return false;
  }
}

export function useInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [dismissed, setDismissed] = useState(dismissedRecently());

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferredPrompt) return { accepted: false, available: false };
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    return { accepted: choice?.outcome === 'accepted', available: true };
  }, [deferredPrompt]);

  const dismiss = useCallback((days = DISMISS_DAYS_DEFAULT) => {
    try {
      const until = Date.now() + days * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISS_KEY, String(until));
      setDismissed(true);
    } catch (_err) {
      setDismissed(true);
    }
  }, []);

  return {
    /** True when the app is already running standalone (no need to prompt). */
    installed,
    /** True when the user dismissed the prompt within the last N days. */
    dismissed,
    /** True on iOS (Safari) — show manual Add-to-Home-Screen instructions. */
    isIOS: isIOS(),
    /** PW-2 — True on Safari for Mac (File → Add to Dock). */
    isMacSafari: isMacSafari(),
    /** True when the Chromium `beforeinstallprompt` event has fired. */
    canPromptAutomatically: !!deferredPrompt,
    /** Fire the automatic install prompt; returns choice outcome. */
    promptInstall,
    /** Hide the prompt for N days (default 7). */
    dismiss,
  };
}
